import { httpApi } from '@app/api/http.api'

import type { DeviceKind, DocumentationFileItem, UsageElement } from '@/features/wizard-page/types'

export type ResetTableData = { index: number; bitIndex: string; dataType: string; defaultValue: string }[]

export type DeviceTemplate = {
  id: string
  name: string
  createdAt: Date
  dataTypes: string
  deviceStatusDataType: string
  deviceStatusIndex: number
  isDeleted: boolean
  isNew: boolean
  resetTableData?: ResetTableData
  monitoredElementParams?: UsageElement[]
  resourceDataType: string
  resourceIndex: number | null
  deviceBitIndex: number
  resourceLabel: string
  deviceKind: DeviceKind
  documentationFiles?: DocumentationFileItem[]
  // The legacy drag-and-drop creator persists heterogeneous component configs.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  templateConfig: any
  updatedAt: Date
  language: string
}

export type GetTemplateForDevicePayload = {
  deviceId: string
  language: string
}

export type AllTemplatesListFormResponse = {
  id: string
  name: string
  language: string
}

export type TemplateUpsertRequest = {
  name: string
  language: string
  isNew?: boolean
  is_deleted?: boolean
  templateConfig?: Record<string, unknown>
  dataTypes?: string
  deviceStatusDataType?: string
  deviceStatusIndex?: number | null
  deviceBitIndex?: number | null
  resourceLabel?: string
  resourceIndex?: number | null
  resourceDataType?: string
  resetTableData?: ResetTableData
  monitoredElementParams?: UsageElement[]
  deviceKind?: DeviceKind
  documentationFiles?: DocumentationFileItem[]
}

export type TableTemplate = {
  id: string
  name: string
  updatedAt: Date
  createdAt: Date
  devices: { id: string; name: string }[]
  language: string
}

const TEMPLATES = 'templates'

const TEMPLATE_UPSERT_TIMEOUT_MS = 180_000

export const updateTemplate = ({ id, ...data }: TemplateUpsertRequest & { id: string }): Promise<void> =>
  httpApi
    .post<void>(`${TEMPLATES}/updateTemplate/${id}`, { id, ...data }, { timeout: TEMPLATE_UPSERT_TIMEOUT_MS })
    .then(({ data }) => data)

export const createTemplate = (data: TemplateUpsertRequest): Promise<string> =>
  httpApi
    .post<string>(`${TEMPLATES}/createTemplate`, data, { timeout: TEMPLATE_UPSERT_TIMEOUT_MS })
    .then(({ data }) => data)

export const getAllTemplatesListForm = (): Promise<AllTemplatesListFormResponse[]> =>
  httpApi.get<AllTemplatesListFormResponse[]>(`${TEMPLATES}/form-list`).then(({ data }) => data)

export const getAllTemplatesTable = (): Promise<TableTemplate[]> =>
  httpApi.get<TableTemplate[]>(`${TEMPLATES}/all-table`).then(({ data }) => data)

export type TemplateForDeviceResponse = {
  id: string
  name: string
  language: string
  deviceStatusDataType: string | null
  deviceStatusIndex: number | null
  deviceBitIndex: number | null
  templateConfig: DeviceTemplate['templateConfig']
  resetTableData?: ResetTableData
  documentationFiles?: DocumentationFileItem[]
}

export const getFullTemplate = (id: string): Promise<DeviceTemplate> =>
  httpApi.get<DeviceTemplate>(`${TEMPLATES}/${id}`).then(({ data }) => data)

export const getTemplateDocumentation = (id: string): Promise<DocumentationFileItem[]> =>
  httpApi
    .get<{ documentationFiles: DocumentationFileItem[] }>(`${TEMPLATES}/${id}/documentation`)
    .then(({ data }) => data.documentationFiles)

export const getTemplateResetTable = (id: string): Promise<ResetTableData> =>
  httpApi
    .get<{ resetTableData: ResetTableData }>(`${TEMPLATES}/${id}/reset-data`)
    .then(({ data }) => data.resetTableData)

export const getTemplateForDevice = (payload: GetTemplateForDevicePayload): Promise<TemplateForDeviceResponse> =>
  httpApi.post<TemplateForDeviceResponse>(`${TEMPLATES}/by-device`, payload).then(({ data }) => data)
